"use client";

import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import Image from "next/image";
import Link from "next/link";
import { useRef, type ReactNode, type RefObject } from "react";

import { resolvePageImage, type SitePageImageMap } from "@/lib/site-page-images";

gsap.registerPlugin(ScrollTrigger);

const accent = "text-[#ff5c00]";

function HighlightCard({
  title,
  children,
  href,
  ctaLabel,
}: {
  title: string;
  children: ReactNode;
  href: string;
  ctaLabel: string;
}) {
  return (
    <article className="flex h-full min-h-0 flex-col overflow-y-auto overscroll-contain rounded-[1.75rem] bg-white p-6 shadow-sm sm:p-8 md:rounded-4xl md:p-10 lg:rounded-[2.25rem]">
      <p className={`text-2xl font-medium leading-none sm:text-3xl md:text-4xl ${accent}`} aria-hidden>
        *
      </p>
      <h3 className={`mt-4 text-xl font-semibold tracking-tight sm:mt-6 sm:text-2xl md:text-3xl ${accent}`}>
        {title}
      </h3>
      <p className="mt-3 flex-1 text-sm leading-relaxed text-[#231f20]/90 sm:mt-4 sm:text-base md:text-lg">
        {children}
      </p>
      <Link
        href={href}
        className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-[#231f20] underline-offset-4 transition-opacity hover:opacity-70 sm:mt-6 sm:text-base md:mt-8 md:text-lg lg:mt-auto lg:pt-6"
      >
        <span aria-hidden className="font-normal">
          &gt;
        </span>
        {ctaLabel}
      </Link>
    </article>
  );
}

function ImageCard({
  src,
  alt,
  priority,
  loading,
  slideRef,
}: {
  src: string;
  alt: string;
  priority?: boolean;
  loading?: "eager" | "lazy";
  slideRef?: RefObject<HTMLDivElement | null>;
}) {
  return (
    <div
      ref={slideRef}
      className="relative h-full min-h-0 w-full overflow-hidden rounded-[1.75rem] bg-[#f5f2f0] will-change-transform md:rounded-4xl lg:rounded-[2.25rem]"
    >
      <div className="relative h-full min-h-0 w-full">
        <Image
          src={src}
          alt={alt}
          fill
          priority={priority}
          loading={priority ? undefined : loading}
          className="object-cover"
          sizes="(max-width: 1023px) 100vw, 33vw"
        />
      </div>
    </div>
  );
}

/** Free digital report page: same mosaic + scroll parallax as home WebDesignDevSection, report copy. */
export function ReportValueSection({
  pageImageMap,
}: {
  pageImageMap?: SitePageImageMap;
}) {
  const value1 = resolvePageImage("free-digital-report-value-1", pageImageMap);
  const value2 = resolvePageImage("free-digital-report-value-2", pageImageMap);
  const value3 = resolvePageImage("free-digital-report-value-3", pageImageMap);

  const scope = useRef<HTMLElement | null>(null);
  const topRowTriggerRef = useRef<HTMLDivElement | null>(null);
  const bottomRowTriggerRef = useRef<HTMLDivElement | null>(null);
  const thirdRowTriggerRef = useRef<HTMLDivElement | null>(null);
  const topSlideRef = useRef<HTMLDivElement | null>(null);
  const bottomSlideRef = useRef<HTMLDivElement | null>(null);
  const thirdSlideRef = useRef<HTMLDivElement | null>(null);

  useGSAP(
    () => {
      const topRowTrigger = topRowTriggerRef.current;
      const bottomRowTrigger = bottomRowTriggerRef.current;
      const thirdRowTrigger = thirdRowTriggerRef.current;
      const topSlide = topSlideRef.current;
      const bottomSlide = bottomSlideRef.current;
      const thirdSlide = thirdSlideRef.current;
      if (
        !topRowTrigger ||
        !bottomRowTrigger ||
        !thirdRowTrigger ||
        !topSlide ||
        !bottomSlide ||
        !thirdSlide
      ) {
        return;
      }

      const mm = gsap.matchMedia();

      mm.add(
        {
          reduceMotion: "(prefers-reduced-motion: reduce)",
          isDesktop: "(min-width: 1024px)",
        },
        (context) => {
          const { reduceMotion, isDesktop } = context.conditions as {
            reduceMotion: boolean;
            isDesktop: boolean;
          };

          const restTop = 0;
          const restBottom = 0;

          if (!isDesktop) {
            gsap.set(topSlide, { xPercent: 0 });
            gsap.set(bottomSlide, { xPercent: 0 });
            gsap.set(thirdSlide, { xPercent: 0 });
            return;
          }
          if (reduceMotion) {
            gsap.set(topSlide, { xPercent: restTop });
            gsap.set(bottomSlide, { xPercent: restBottom });
            gsap.set(thirdSlide, { xPercent: restTop });
            return;
          }

          const rowScrollTrigger = (trigger: HTMLElement) => ({
            trigger,
            start: "top 88%",
            end: "top 60%",
            scrub: 1.15,
            invalidateOnRefresh: true,
          });

          gsap.fromTo(
            topSlide,
            { xPercent: 109 },
            {
              xPercent: restTop,
              ease: "none",
              scrollTrigger: rowScrollTrigger(topRowTrigger),
            },
          );
          gsap.fromTo(
            bottomSlide,
            { xPercent: -109 },
            {
              xPercent: restBottom,
              ease: "none",
              scrollTrigger: rowScrollTrigger(bottomRowTrigger),
            },
          );
          gsap.fromTo(
            thirdSlide,
            { xPercent: 109 },
            {
              xPercent: restTop,
              ease: "none",
              scrollTrigger: rowScrollTrigger(thirdRowTrigger),
            },
          );
        },
      );

      return () => {
        mm.revert();
      };
    },
    { scope, dependencies: [] },
  );

  return (
    <section
      ref={scope}
      className="relative bg-[#b5adaa] pt-16 pb-20 font-sans text-[#231f20] md:pt-20 md:pb-28"
      aria-labelledby="free-report-value-heading"
    >
      <div className="site-content">
        <div className="mt-[clamp(3rem,18vw,31.25rem)] grid grid-cols-1 gap-8 lg:mt-[clamp(26rem,26vw,42rem)] lg:grid-cols-12 lg:gap-16 xl:gap-20">
          <div className="max-w-[550px] lg:col-span-7">
            <p className="text-sm font-medium tracking-wide text-[#231f20]/80 md:text-base">
              What you receive
            </p>
            <h2
              id="free-report-value-heading"
              className="mt-4 text-4xl leading-[0.95] tracking-tight sm:text-5xl lg:text-6xl xl:text-7xl"
            >
              Your report turns noise into a plan.{" "}
              <span className={accent}>So you can act with confidence</span>
            </h2>
          </div>

          <div className="lg:col-span-5">
            <p className={`text-4xl font-medium leading-none md:text-5xl ${accent}`} aria-hidden>
              *
            </p>
            <hr className="mt-4 max-w-full border-0 border-t border-[#231f20]/85" />
            <p className="mt-6 text-base leading-relaxed text-[#231f20]/95 md:text-lg">
              You get a readable snapshot of how people find you, what holds
              attention, and where the funnel frays—pulled together so you are
              not staring at raw exports. The goal is simple: fewer tabs, fewer
              guesses, and a clearer order of operations for what to improve
              next.
            </p>
          </div>
        </div>

        <div className="mt-14 flex flex-col gap-10 md:mt-20 md:gap-12 lg:grid lg:grid-cols-3 lg:items-start lg:gap-x-8 lg:gap-y-10 lg:overflow-visible lg:min-h-[clamp(38rem,52vw,58rem)] lg:grid-rows-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)] xl:gap-x-10 xl:gap-y-10 xl:min-h-[clamp(40rem,48vw,60rem)]">
          <div className="flex w-full flex-col gap-4 sm:gap-5 lg:contents">
            <div
              ref={topRowTriggerRef}
              className="aspect-square w-full shrink-0 lg:relative lg:z-10 lg:col-start-2 lg:row-start-1 lg:aspect-auto lg:h-full lg:min-h-0 lg:w-auto lg:max-w-none"
            >
              <ImageCard
                slideRef={topSlideRef}
                priority
                src={value1.src}
                alt={value1.alt}
              />
            </div>
            <div className="aspect-square w-full shrink-0 lg:relative lg:z-30 lg:col-start-3 lg:row-start-1 lg:aspect-auto lg:h-full lg:min-h-0 lg:w-auto lg:max-w-none lg:-translate-x-[14%] xl:translate-x-0">
              <HighlightCard
                title="Channel & visibility snapshot"
                href="#free-report-channel-snapshot"
                ctaLabel="See what we review"
              >
                How you show up across search, social, and key landing
                experiences—so you can see where discovery works, where
                messaging drifts, and which surfaces deserve the next round of
                attention.
              </HighlightCard>
            </div>
          </div>
          <div className="flex w-full flex-col gap-4 sm:gap-5 lg:contents">
            <div
              ref={bottomRowTriggerRef}
              className="aspect-square w-full shrink-0 lg:relative lg:z-10 lg:col-start-2 lg:row-start-2 lg:aspect-auto lg:h-full lg:min-h-0 lg:w-auto lg:max-w-none"
            >
              <ImageCard
                slideRef={bottomSlideRef}
                loading="eager"
                src={value2.src}
                alt={value2.alt}
              />
            </div>
            <div className="aspect-square w-full shrink-0 lg:relative lg:z-30 lg:col-start-1 lg:row-start-2 lg:aspect-auto lg:h-full lg:min-h-0 lg:w-auto lg:max-w-none lg:translate-x-[14%] xl:translate-x-0">
              <HighlightCard
                title="Prioritized recommendations"
                href="#free-report-priorities"
                ctaLabel="See how we prioritize"
              >
                A short list of what to test, fix, or scale first—grounded in
                behaviour and outcomes, not vanity metrics. Enough direction to
                move this week; enough context to align stakeholders who only
                have five minutes.
              </HighlightCard>
            </div>
          </div>
          <div className="flex w-full flex-col gap-4 sm:gap-5 lg:contents">
            <div
              ref={thirdRowTriggerRef}
              className="aspect-square w-full shrink-0 lg:relative lg:z-10 lg:col-start-2 lg:row-start-3 lg:aspect-auto lg:h-full lg:min-h-0 lg:w-auto lg:max-w-none"
            >
              <ImageCard
                slideRef={thirdSlideRef}
                loading="eager"
                src={value3.src}
                alt={value3.alt}
              />
            </div>
            <div className="aspect-square w-full shrink-0 lg:relative lg:z-30 lg:col-start-3 lg:row-start-3 lg:aspect-auto lg:h-full lg:min-h-0 lg:w-auto lg:max-w-none lg:-translate-x-[14%] xl:translate-x-0">
              <HighlightCard
                title="Audience & intent signals"
                href="#free-report-audience-intent"
                ctaLabel="See how we read intent"
              >
                Who is arriving, what they are trying to solve, and where
                friction shows up in the journey—so you can align messaging,
                offers, and UX with real behaviour instead of assumptions.
              </HighlightCard>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
